import clsx from "clsx";
import { useMemo } from "react";

// Material UI
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridColDef,
	GridColumnHeaderParams,
	GridRowClassNameParams,
	useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Box, SvgIcon } from "@mui/material";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { SelectionAndFilterCheckbox } from "../SelectionAndFilterCheckbox";

// Icons
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import { ReactComponent as ThumbDownIcon } from "@/assets/icons/actions/ThumbDownIcon.svg";
import { ReactComponent as CheckIcon } from "@/assets/icons/common/reviewed-check.svg";

// Hooks
import { useLocations } from "@/Common/hooks/Location/useLocations";

// Helpers
import { getTenantParameters } from "@/Common/helpers/Tenant";
import { getFormatDate } from "@/Common/helpers/Dates";

//Constants
import { DATE_FORMAT } from "@/Constants/date";
import { CELL_CLASS_NAME, CUSTOM_PROPS, SLOTS } from "./commonProps";

// Types
import { TDealerInventoryTrade } from "@/DataPages/components/Inventory/types/dealerTrades";
import { defaultCellRenderer } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { buildLinkedRowFilterFn, linkedRowsComparatorFnBuilder } from "@/Common/helpers/Tables/linkedRows";
import { createTransferCounterparts } from "../helpers";
import { wrapFilterOperators, wrapQuickFilter } from "@/Common/helpers/mui/filters";

// Constants
import { HISTORY_TRANSFERS_TABLE, InventoryTab, TRANSFER_ASSET_SWAP } from "../constants";
import { usePersistentSelection } from "@/Common/hooks/selection/usePersistentSelection";
import { SelectionAndFilterCheckboxAll } from "../SelectionAndFilterAllCheckbox";

const renderHeaderHandler = (params: GridColumnHeaderParams) => <TypographyHeader fontSize="12px" fontLineHeight="14px" params={params} />;

type TTransferRequestHistoryTableProps = {
	locationId: string;
	data?: TDealerInventoryTrade[];
};

export const TransferRequestHistoryTable = (props: Readonly<TTransferRequestHistoryTableProps>) => {
	const { locationId, data: rows = [] } = props;

	const apiRef = useGridApiRef();

	const { locations } = useLocations(getTenantParameters(locationId)?.tenant);
	const mappedLocations = locations?.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.locationId]: item.name }), {});
	const { selectedIds } = usePersistentSelection({ table: HISTORY_TRANSFERS_TABLE });

	const { swapCounterparts, links } = useMemo(() => createTransferCounterparts(rows, locationId), [rows, locationId]);

	const columns: GridColDef[] = useMemo(() => {
		const linkedFilterFn = buildLinkedRowFilterFn<TDealerInventoryTrade>(links);

		const wrappedOperators = wrapFilterOperators(linkedFilterFn);
		const getApplyQuickFilterFn = wrapQuickFilter(linkedFilterFn);

		return (
			[
				{
					field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
					width: 50,
					filterable: false,
					sortable: false,
					resizable: false,
					renderHeader: () => (
						<SelectionAndFilterCheckboxAll
							className="!py-1"
							table={HISTORY_TRANSFERS_TABLE}
							data={rows}
							origin={InventoryTab.DEALER_TRADES}
							pathToVinNumber="vehicle.vinNumber"
						/>
					),
					renderCell: (params) => {
						return (
							<SelectionAndFilterCheckbox
								_id={params.row._id as string}
								table={HISTORY_TRANSFERS_TABLE}
								row={params.row}
								origin={InventoryTab.DEALER_TRADES}
								pathToVinNumber="vehicle.vinNumber"
								className="!ml-[-1.5px]"
							/>
						);
					},
				},
				{
					field: "_id",
					headerName: "ID",
					minWidth: 10,
					width: 10,
					filterable: false,
					resizable: false,
				},
				{
					field: "originLocationId",
					flex: 1,
					minWidth: 70,
					headerName: "Origin Store",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (value: string) => {
						const tenantParameters = getTenantParameters(value);
						return `${tenantParameters?.store}/${tenantParameters?.branch}`;
					},
					renderCell: (params) => {
						return (
							<TypographyCell
								tooltipProps={{
									title: `Doing Business As: ${mappedLocations?.[params.row.originLocationId]}`,
									placement: "bottom",
								}}
								hasTooltip
								forceTooltip
								params={params}
							/>
						);
					},
				},
				{
					field: "destinationLocationId",
					flex: 1,
					minWidth: 100,
					headerName: "Destination Store",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (value: string) => {
						const tenantParameters = getTenantParameters(value);
						return `${tenantParameters?.store}/${tenantParameters?.branch}`;
					},
					renderCell: (params) => {
						return (
							<TypographyCell
								tooltipProps={{
									title: `Doing Business As: ${mappedLocations?.[params.row.destinationLocationId]}`,
									placement: "bottom",
								}}
								hasTooltip
								forceTooltip
								params={params}
							/>
						);
					},
				},
				{
					field: "stockNumber",
					headerName: "Stock #",
					width: 80,
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.stockNumber ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "vinNumber",
					width: 80,
					headerName: "VIN",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.vinNumber ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "make",
					width: 100,
					headerName: "Make",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.make ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "model",
					width: 90,
					headerName: "Model",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.model ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "trim",
					width: 95,
					headerName: "Trim",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.trim ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "colorExterior",
					width: 75,
					headerName: "Ext. Color",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.colorExterior ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "year",
					width: 60,
					headerName: "Year",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row) => row?.vehicle?.year ?? "",
					renderCell: defaultCellRenderer,
				},
				{
					field: "requestedBy",
					flex: 1,
					minWidth: 110,
					headerName: "Requested By",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row: TDealerInventoryTrade) => row.requestStatus.requestedBy,
					renderCell: defaultCellRenderer,
				},
				{
					field: "requestedDate",
					width: 95,
					headerName: "Requested Date",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row: TDealerInventoryTrade) =>
						getFormatDate(row.requestStatus.requestedDate, DATE_FORMAT.monthAbbrDatePadded),
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
					renderCell: defaultCellRenderer,
				},
				{
					field: "statusDate",
					width: 95,
					headerName: "Status Date",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row: TDealerInventoryTrade) =>
						getFormatDate(row.requestStatus.statusDate, DATE_FORMAT.monthAbbrDatePadded),
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
					renderCell: defaultCellRenderer,
				},
				{
					field: "requestStatus",
					width: 95,
					headerName: "Request Status",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row: TDealerInventoryTrade) => row.requestStatus.status,
					renderCell: (params) => {
						const { value } = params;
						const icons: Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
							Completed: CheckIcon,
							Cancelled: TrashIcon,
							Rejected: ThumbDownIcon,
						};

						const resolver = params.row.requestStatus.resolvedBy;
						const resolvedBy = `${value} by ${resolver}`;
						const rejectReason = value !== "Completed" ? params.row.requestStatus.rejectReason : "";

						const titleToRender = (
							<Box>
								{resolvedBy && <p>{resolvedBy}</p>}
								{rejectReason && <p>{rejectReason}</p>}
							</Box>
						);

						return (
							<TypographyCell
								tooltipProps={{
									title: titleToRender,
									placement: "bottom",
								}}
								hasTooltip
								forceTooltip
							>
								<SvgIcon
									className={clsx({
										"mr-[5px]": true,
										"text-default": value === "Cancelled",
										"text-alert": value === "Rejected",
										"text-success": value === "Completed",
									})}
									fontSize="small"
									component={icons[value]}
								/>
								{value}
							</TypographyCell>
						);
					},
				},
				{
					field: "transferType",
					width: 80,
					headerName: "Transfer Type",
					renderHeader: renderHeaderHandler,
					cellClassName: () => CELL_CLASS_NAME,
					renderCell: defaultCellRenderer,
				},
				{
					field: "transferAmount",
					width: 85,
					headerName: "Transfer Amount",
					renderHeader: renderHeaderHandler,
					type: "number",
					align: "right",
					cellClassName: () => CELL_CLASS_NAME,
					valueGetter: (_, row: TDealerInventoryTrade) => row.vehicle.transferAmount,
					renderCell: (params) => {
						return <TypographyCell render="absolute" params={params} hints={{ isMoney: true, noFocus: true, noWrap: true }} />;
					},
				},
			] as GridColDef[]
		).map(
			(colDef) =>
				({
					...colDef,
					getApplyQuickFilterFn,
					filterOperators: wrappedOperators[colDef.type ?? "string"],
					getSortComparator: (sortDirection) =>
						linkedRowsComparatorFnBuilder(sortDirection, colDef, swapCounterparts, (_v1, _v2, row1) =>
							row1.originLocationId === row1.requestedLocationId ? -1 : 1
						),
				} as GridColDef)
		);
	}, [swapCounterparts, links, rows]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				filter: {
					data: rows || [],
					hide: false,
					disabled: false,
				},
				search: {
					useQuickFilter: true,
					data: rows,
					hide: false,
				},
			},
			noRowsOverlay: {
				title: "No Inventory Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Inventory Data",
				className: "!text-montserrat",
			},
		}),
		[rows, selectedIds]
	);

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={columns}
			rows={rows || []}
			maxRows={10}
			enableCustomScrollbars
			disableColumnMenu
			slots={SLOTS}
			slotProps={slotProps}
			hideFooter
			customProps={CUSTOM_PROPS}
			rowHeight={25}
			autoHeight={false}
			columnVisibilityModel={{ _id: false }}
			checkboxSelection
			getRowClassName={(params) => {
				const { row } = params as GridRowClassNameParams<TDealerInventoryTrade>;

				if (row.transferType !== TRANSFER_ASSET_SWAP) {
					return "!border-l-[1px] !border-l-[transparent]";
				}
				const extraBorder = row.destinationLocationId === row.requestedByLocationId ? "!border-b-0" : "!border-t-0";
				return `!border-[#ccc] !border-[1px] ${extraBorder}`;
			}}
		/>
	);
};
